import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Button, Chip, FormControlLabel, Switch, Tooltip, } from "@mui/material";
import { DocumentContentActions } from "../documentsApi"
import { unwrapResult } from "@reduxjs/toolkit";
import { DocumentAnalyzedObject, DocumentAnswerType, DocumentQuestion, DocumentQuestionAnswer, loadAnalyzedObjectsForSection, loadDocumentSectionQuestions } from "./documentQuestionsApi"
import { AppId } from "../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useCallback, useEffect, useState } from "react";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { AddEditDocumentQuestion } from "./dialogs/AddEditDocumentQuestion";
import { Edit, Delete, Add } from "@mui/icons-material";
import { Localized } from "@fluent/react";
import produce from "immer";
import { If } from "../../../app/If";
import { DeleteDocumentQuestion } from "./dialogs/DeleteDocumentQuestion";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { AddEditAnalyzedObject } from "./dialogs/AddEditAnalyzedObject";
import { DeleteDocumentAnalyzedObject } from "./dialogs/DeleteDocumentAnalyzedObject";
import { LpField } from "../../../app/components/LpField";
import { PrintPreviewQuestionsObjectsView } from "./PrintPreviewDocumentQuestion";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import CommentIcon from '@mui/icons-material/Comment'
import { isDocEditable, selectDocument, showDocComments } from "../documentSlice";
import { AllCommentsCounter } from "../CommentsCounter";
import { impossible } from "../../../app/Utils";
import { LoadModulesForACMDialog } from "./dialogs/LoadModulesForACMDialog";
import { LoadProcessesForACPDialog } from "./dialogs/LoadProcessesForACPDialog";
import { LoadFunctionalitiesForACFDialog } from "./dialogs/LoadFunctionalitiesForACFDialog";

type DialogState
    = "none"
    | "addEditDocumentQuestion"
    | "deleteDocumentQuestion"
    | "addEditAnalyzedObject"
    | "deleteDocumentAnalyzedObject"
    | "commentDocumentQuestion"
    | "commentEditAnalyzedObject"
    | "loadModulesForACM"
    | "loadProcessesForACP"
    | "loadFunctionalitiesForACF"

export interface DocumentQuestionViewProps {
    documentId: AppId
    sectionId: AppId
    actions: DocumentContentActions[],
    enablePreview?: boolean
}

function getAnswerChip(val: DocumentAnswerType, labelPL = true) {
    switch (val) {
        case "YES": return <Chip label={labelPL ? "Tak" : "Yes"} color="success" size="small" />;
        case "NO": return <Chip label={labelPL ? "Nie" : "No"} color="error" size="small" />;
        case "NA": return <Chip label={labelPL ? "N/D" : "N/A"} size="small" />;
        case "NONE":
        default: return <Chip label={"—"} variant="outlined" size="small" />;
    }
}

function answerChipWithTooltip({
    value,
    comment,
    labelPL = true,
}: { value: DocumentAnswerType; comment: string; labelPL?: boolean }) {
    const chip = getAnswerChip(value, labelPL);
    return comment ? (
        <Tooltip
            title={
                <>
                    <Localized id="comment">
                        <span>_comment</span>
                    </Localized>
                    {": "}
                    <em>{comment}</em>
                </>
            }
            arrow
        >
            {chip}
        </Tooltip>
    ) : (
        chip
    );
}

function renderAnswer(
    answers: DocumentQuestionAnswer[] | undefined,
    questionId: string
) {
    const val: DocumentAnswerType =
        answers?.find(a => a.questionId.toString() === questionId.toString())?.value ?? "NONE";

    return getAnswerChip(val);
}

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}

export interface AnalyzedObject {
    id: number;
    name: string;
    answers: Record<number, string | null>;
}

export const DocumentQuestionView = ({ documentId, sectionId, actions, enablePreview = false }: DocumentQuestionViewProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState<DocumentQuestion | null>(null);
    const [selectedAnalyzedObject, setSelectedAnalyzedObject] = useState<DocumentAnalyzedObject | null>(null);
    const [selectedAnalyzedObjectIndex, setSelectedAnalyzedObjectIndex] = useState<number | null>(null);
    const [isLastAnalyzedObject, setIsLastAnalyzedObject] = useState<boolean>(false);
    const [questions, setQuestions] = useState<DocumentQuestion[]>();
    const [analyzedObjects, setAnalyzedObjects] = useState<DocumentAnalyzedObject[]>();
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()

    const [dialog, setDialog] = useState<DialogState>("none")
    const [isPreview, setIsPreview] = useState(false);
    const [showQuestions, setShowQuestions] = useState(true);
    const totalCols = 4 + (questions?.length ?? 0);
    const showComments = useAppSelector(state => showDocComments(state, sectionId))
    const isEditable = useAppSelector(isDocEditable)

    useEffect(() => {
        let ignore = false;
        const loadInitialData = async () => {
            setIsLoading(true);
            setIsError(false);
            setError(null as any);

            try {
                const [questions, analyzedObjects] = await Promise.all([
                    dispatch(loadDocumentSectionQuestions({ documentId, sectionId })).then(unwrapResult),
                    dispatch(loadAnalyzedObjectsForSection({ documentId, sectionId })).then(unwrapResult),
                ]);

                if (ignore) return;
                setQuestions(questions);
                setAnalyzedObjects(analyzedObjects);
            } catch (err) {
                if (ignore) return;
                setIsError(true);
                setError(err as any);
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadInitialData();
        return () => { ignore = true; };
    }, [dispatch, documentId, sectionId]);

    const handleEdit = (row: DocumentQuestion) => {
        setSelectedQuestion(row);
        setDialog("addEditDocumentQuestion");
    };

    const handleDelete = (q: DocumentQuestion) => {
        setDialog("deleteDocumentQuestion");
        setSelectedQuestion(q);
    };

    const handleComments = (row: DocumentQuestion) => {
        setSelectedQuestion(row);
        setDialog("commentDocumentQuestion");
    };

    const handleAddAnalyzedObjectClick = () => {
        setSelectedAnalyzedObject(null);
        setSelectedAnalyzedObjectIndex(analyzedObjects ? analyzedObjects.length : null)
        setDialog("addEditAnalyzedObject");
    }

    const handleLoadModulesForACMClick = () => {
        setSelectedAnalyzedObject(null);
        setDialog("loadModulesForACM");
    }

    const handleLoadProcessesForACPClick = () => {
        setSelectedAnalyzedObject(null);
        setDialog("loadProcessesForACP");
    }

    const handleLoadFunctionalitiesForACFClick = () => {
        setSelectedAnalyzedObject(null);
        setDialog("loadFunctionalitiesForACF");
    }

    const handleDeleteAnalyzedObject = (obj: DocumentAnalyzedObject) => {
        setDialog("deleteDocumentAnalyzedObject");
        setSelectedAnalyzedObject(obj);
    };

    const handleDialogCancelClick = useCallback(() => {
        setDialog("none")
        setSelectedQuestion(null);
    }, [])

    const handleEvaluate = (obj: DocumentAnalyzedObject) => {
        setSelectedAnalyzedObject(obj);
        if (analyzedObjects) {
            const index = analyzedObjects.findIndex(o => o.id === obj.id)
            setIsLastAnalyzedObject(index === analyzedObjects.length - 1);
            setSelectedAnalyzedObjectIndex(index)
        }
        setDialog("addEditAnalyzedObject");
    };

    const handleCommentObject = (obj: DocumentAnalyzedObject) => {
        setSelectedAnalyzedObject(obj);
        if (analyzedObjects) {
            const index = analyzedObjects.findIndex(o => o.id === obj.id)
            setIsLastAnalyzedObject(index === analyzedObjects.length - 1);
            setSelectedAnalyzedObjectIndex(index)
        }
        setDialog("commentEditAnalyzedObject");
    };

    const onSuccessfullySave = (question: DocumentQuestion) => {
        setQuestions(produce(draft => {
            if (draft !== undefined) {
                const index = draft.findIndex(q => q.id === question.id);

                if (index !== -1) {
                    draft[index] = question;
                } else {
                    draft.push(question);
                }
            }
        }));
        setDialog("none")
    };

    const onSuccessfullyAnalyzedObjectSave = (analyzedObject: DocumentAnalyzedObject, close: boolean) => {
        setAnalyzedObjects(produce(draft => {
            if (draft !== undefined) {
                const index = draft.findIndex(o => o.id === analyzedObject.id);

                if (index !== -1) {
                    draft[index] = analyzedObject;
                } else {
                    draft.push(analyzedObject);
                }
            }
        }));
        setSelectedAnalyzedObject(null);
        setSelectedAnalyzedObjectIndex(null);
        if (close) {
            setDialog("none")
        }
    };

    const onSuccessfullyLoadedModules = (analyzedObjects: DocumentAnalyzedObject[]) => {
        setAnalyzedObjects(analyzedObjects);
        setDialog("none")
    };

    const nextAnalyzedObject = useCallback((analyzedObject: DocumentAnalyzedObject) => {
        if (analyzedObjects === undefined) return;

        const index = analyzedObjects.findIndex(o => o.id === analyzedObject.id);
        if (index >= 0 && index < analyzedObjects.length - 1) {
            setSelectedAnalyzedObject(analyzedObjects[index + 1]);
            setSelectedAnalyzedObjectIndex(index + 1)
            setIsLastAnalyzedObject(index + 1 === analyzedObjects.length - 1);
        }
    }, [analyzedObjects])

    const prevAnalyzedObject = useCallback((analyzedObject: DocumentAnalyzedObject) => {
        if (analyzedObjects === undefined) return;

        const index = analyzedObjects.findIndex(o => o.id === analyzedObject.id);
        if (index > 0) {
            setSelectedAnalyzedObject(analyzedObjects[index - 1]);
            setSelectedAnalyzedObjectIndex(index - 1)
            setIsLastAnalyzedObject(false);
        }
    }, [analyzedObjects])

    const onQuestionDeleted = (questionId: AppId) => {
        setQuestions(produce(draft => {
            if (draft !== undefined) {
                const index = draft.findIndex(q => q.id === questionId);
                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }
        }));
        setSelectedQuestion(null);
        setDialog("none");
    };

    const onSuccessfullyAnalyzedObjectDelete = (objectId: number) => {
        setAnalyzedObjects(produce(draft => {
            if (draft !== undefined) {
                const index = draft.findIndex(o => o.id === objectId);
                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }
        }));
        setSelectedAnalyzedObject(null);
        setSelectedAnalyzedObjectIndex(null);
        setDialog("none");
    };

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}>
            {enablePreview && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showQuestions}
                                onChange={() => setShowQuestions(prev => !prev)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={showQuestions ?
                            <Localized id="document-question-hide">
                                <span>_hide</span>
                            </Localized> :
                            <Localized id="document-question-show">
                                <span>_show</span>
                            </Localized>}
                    />
                    <FormControlLabel
                        control={<Switch checked={isPreview} size="small" onChange={() => setIsPreview(v => !v)} />}
                        label={<Localized id="print-preview">
                            <span>_Print preview</span>
                        </Localized>}

                    />
                </Box>
            )}
            {enablePreview && isPreview ? (
                <PrintPreviewQuestionsObjectsView
                    questions={questions ?? []}
                    analyzedObjects={analyzedObjects ?? []}
                />
            ) : (
                <>
                    {showQuestions && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <FactCheckIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                                <Localized id="document-question-table-title">
                                                    <span>_Definicje pytań</span>
                                                </Localized>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <Localized id="lp">
                                                <span>_lp</span>
                                            </Localized>
                                        </TableCell>
                                        <TableCell>
                                            <Localized id="document-question">
                                                <span>_question</span>
                                            </Localized>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {questions?.map((q) => (
                                        <TableRow key={q.documentId + "_" + q.text} hover>
                                            <TableCell sx={{ width: "10px", maxWidth: "20px" }}>{q.order}</TableCell>
                                            <TableCell>{q.text}</TableCell>
                                            <TableCell align="right">
                                                <If condition={hasAction(actions, "Edit")}>
                                                    <>
                                                        <Tooltip title={<Localized id='edit'>_edit</Localized>}>
                                                            <IconButton
                                                                onClick={() => handleEdit(q)}
                                                                sx={{
                                                                    color: "action.active",
                                                                    "&:hover": { color: "info.main" }
                                                                }}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={<Localized id='delete'>_delete</Localized>}>
                                                            <IconButton
                                                                onClick={() => handleDelete(q)}
                                                                sx={{
                                                                    color: "action.active",
                                                                    "&:hover": { color: "error.main" }
                                                                }}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                </If>
                                                {showComments && <AllCommentsCounter sectionId={sectionId} contextGuid={q.guid}>
                                                    <Tooltip title={<Localized id='comments'>Komentarze</Localized>}>
                                                        <IconButton
                                                            onClick={() => handleComments(q)}
                                                            sx={{
                                                                color: "action.active",
                                                                "&:hover": { color: "info.main" }
                                                            }}
                                                        >
                                                            <CommentIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </AllCommentsCounter>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Button
                                variant="contained"
                                disabled={!isEditable}
                                color="secondary"
                                startIcon={<Add />}
                                size="small"
                                sx={{ m: 1 }}
                                onClick={() => setDialog("addEditDocumentQuestion")}
                            >
                                <Localized id="document-question-add">
                                    <span>_Add question</span>
                                </Localized>
                            </Button>
                        </TableContainer>
                    )}
                    <TableContainer component={Paper} >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell colSpan={totalCols} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <ManageSearchIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                            <Localized id="document-analyzed-objects-title">
                                                <span>_objects</span>
                                            </Localized>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ width: 44, minWidth: 44, maxWidth: 44, px: 1 }}>
                                        <Localized id="lp">
                                            <span>_lp</span>
                                        </Localized>
                                    </TableCell>
                                    <TableCell>
                                        <Localized id="name">
                                            <span>_name</span>
                                        </Localized>
                                    </TableCell>
                                    {questions?.map((q) => (
                                        <TableCell key={q.id} sx={{ width: 36, textAlign: "center" }}>{q.order} </TableCell>
                                    ))}
                                    <TableCell sx={{ width: 36, textAlign: "center" }}>
                                        <Localized id="note">
                                            <span>_note</span>
                                        </Localized>
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 180, minWidth: 180, maxWidth: 180 }}  ></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {analyzedObjects?.map((obj, index) => (
                                    <TableRow key={obj.id + "_" + index} hover>
                                        <TableCell sx={{ width: 44, minWidth: 44, maxWidth: 44, px: 1 }}>
                                            <LpField value={index + 1} />
                                        </TableCell>

                                        <TableCell>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                <Box sx={{ fontWeight: 500 }}>{obj.name}</Box>
                                                {obj.description && (
                                                    <Box
                                                        sx={{
                                                            color: "text.secondary",
                                                            fontSize: "0.8125rem",
                                                            lineHeight: 1.35,
                                                            "& p": { m: 0 },
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: obj.description }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        {questions?.map((q) => (
                                            <TableCell key={q.id} sx={{ width: 36, textAlign: "center" }}>
                                                {renderAnswer(obj.answers, q.id)}
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{ width: 36, textAlign: "center" }}>
                                            {answerChipWithTooltip({ value: obj.result, comment: obj.resultComment })}
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <If condition={hasAction(actions, "Edit")}>
                                                <>
                                                    <Tooltip title={<Localized id='evaluate'>_evaluate</Localized>}>
                                                        <IconButton
                                                            onClick={() => handleEvaluate(obj)}
                                                            sx={{
                                                                color: "action.active",
                                                                "&:hover": { color: "info.main" }
                                                            }}
                                                        >
                                                            <AssignmentTurnedInIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={<Localized id='delete'>_delete</Localized>}>
                                                        <IconButton
                                                            onClick={() => handleDeleteAnalyzedObject(obj)}
                                                            sx={{
                                                                color: "action.active",
                                                                "&:hover": { color: "error.main" }
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            </If>
                                            {showComments && <AllCommentsCounter sectionId={sectionId} contextGuid={obj.guid}>
                                                <Tooltip title={<Localized id='comments'>Komentarze</Localized>}>
                                                    <IconButton
                                                        onClick={() => handleCommentObject(obj)}
                                                        sx={{
                                                            color: "action.active",
                                                            "&:hover": { color: "info.main" }
                                                        }}
                                                    >
                                                        <CommentIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </AllCommentsCounter>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <If condition={isEditable}>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={!isEditable}
                                startIcon={<Add />}
                                size="small"
                                sx={{ m: 1 }}
                                onClick={() => handleAddAnalyzedObjectClick()}
                            >
                                <Localized id="document-analyzed-objects-add">
                                    <span>_Add analyzed object</span>
                                </Localized>
                            </Button>
                            <If condition={header.type === "ACM"}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={!isEditable}
                                    startIcon={<Add />}
                                    size="small"
                                    sx={{ m: 1 }}
                                    onClick={() => handleLoadModulesForACMClick()}
                                >
                                    <Localized id="document-analyzed-objects-load-modules">
                                        <span>_Load modules for ACM</span>
                                    </Localized>
                                </Button>
                            </If>
                            <If condition={header.type === "ACP"}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={!isEditable}
                                    startIcon={<Add />}
                                    size="small"
                                    sx={{ m: 1 }}
                                    onClick={() => handleLoadProcessesForACPClick()}
                                >
                                    <Localized id="document-analyzed-objects-load-processes">
                                        <span>_Load processes for ACP</span>
                                    </Localized>
                                </Button>
                            </If>
                            <If condition={header.type === "ACF"}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={!isEditable}
                                    startIcon={<Add />}
                                    size="small"
                                    sx={{ m: 1 }}
                                    onClick={() => handleLoadFunctionalitiesForACFClick()}
                                >
                                    <Localized id="document-analyzed-objects-load-functionalities">
                                        <span>_Load functionalities for ACF</span>
                                    </Localized>
                                </Button>
                            </If>
                        </If>
                    </TableContainer>
                    {
                        dialog === "addEditDocumentQuestion" &&
                        <AddEditDocumentQuestion
                            documentId={documentId}
                            sectionId={sectionId}
                            mode={selectedQuestion ? "edit" : "create"}
                            questionRow={selectedQuestion}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullySave}
                        />
                    }
                    {
                        dialog === "commentDocumentQuestion" &&
                        <AddEditDocumentQuestion
                            documentId={documentId}
                            sectionId={sectionId}
                            mode='comment'
                            questionRow={selectedQuestion}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullySave}
                        />
                    }
                    {
                        dialog === "deleteDocumentQuestion" && selectedQuestion?.id &&
                        <DeleteDocumentQuestion
                            documentId={documentId}
                            sectionId={sectionId}
                            questionId={selectedQuestion?.id}
                            onSuccessfulSave={onQuestionDeleted}
                            onCancel={handleDialogCancelClick}
                        />
                    }
                    {
                        dialog === "addEditAnalyzedObject" &&
                        <AddEditAnalyzedObject
                            documentId={documentId}
                            sectionId={sectionId}
                            mode={selectedAnalyzedObject ? "edit" : "create"}
                            questionsDefinitions={questions || []}
                            analyzedObjectRow={selectedAnalyzedObject}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullyAnalyzedObjectSave}
                            next={nextAnalyzedObject}
                            back={prevAnalyzedObject}
                            lastItem={isLastAnalyzedObject}
                            index={selectedAnalyzedObjectIndex}
                            total={analyzedObjects ? analyzedObjects.length : 0}
                        />
                    }
                    {
                        dialog === "loadModulesForACM" &&
                        <LoadModulesForACMDialog
                            documentId={documentId}
                            sectionId={sectionId}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullyLoadedModules}
                        />
                    }
                    {
                        dialog === "loadProcessesForACP" &&
                        <LoadProcessesForACPDialog
                            documentId={documentId}
                            sectionId={sectionId}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullyLoadedModules}
                        />
                    }
                    {
                        dialog === "loadFunctionalitiesForACF" &&
                        <LoadFunctionalitiesForACFDialog
                            documentId={documentId}
                            sectionId={sectionId}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullyLoadedModules}
                        />
                    }
                    {
                        dialog === "commentEditAnalyzedObject" &&
                        <AddEditAnalyzedObject
                            documentId={documentId}
                            sectionId={sectionId}
                            mode='comment'
                            questionsDefinitions={questions || []}
                            analyzedObjectRow={selectedAnalyzedObject}
                            onCancelClick={handleDialogCancelClick}
                            onSuccessfulSave={onSuccessfullyAnalyzedObjectSave}
                            next={nextAnalyzedObject}
                            back={prevAnalyzedObject}
                            lastItem={isLastAnalyzedObject}
                            index={selectedAnalyzedObjectIndex}
                            total={analyzedObjects ? analyzedObjects.length : 0}
                        />
                    }
                    {
                        dialog === "deleteDocumentAnalyzedObject" && selectedAnalyzedObject?.id &&
                        <DeleteDocumentAnalyzedObject
                            documentId={documentId}
                            sectionId={sectionId}
                            objectId={selectedAnalyzedObject?.id}
                            onSuccessfulSave={onSuccessfullyAnalyzedObjectDelete}
                            onCancel={handleDialogCancelClick}
                        />
                    }
                </>
            )}
        </Box>
    );
};
